/**
 * Network configuration for Poker Reborn
 *
 * Based on GL protocol from legacy AS3 clients.
 * See LEGACY_DATA_USAGE.md for source tracking.
 */

// =============================================================================
// SERVER ENDPOINTS
// =============================================================================

/** Server configuration */
export const SERVERS = {
  /** Staging server */
  staging: {
    wsUrl: 'wss://staging.pokerreborn.com/gl',
    httpUrl: 'https://staging.pokerreborn.com',
  },

  /** Production server */
  production: {
    wsUrl: 'wss://play.pokerreborn.com/gl',
    httpUrl: 'https://play.pokerreborn.com',
  },
};

/** Server config type */
export interface ServerConfig {
  wsUrl: string;
  httpUrl: string;
}

/** Get server config for current environment */
export function getServerConfig(): ServerConfig {
  // VITE_WS_URL override — essential for Capacitor dev builds where
  // window.location.host resolves to 'localhost' instead of the dev server
  const wsUrlOverride = import.meta.env.VITE_WS_URL as string | undefined;
  if (wsUrlOverride) {
    const httpUrl = wsUrlOverride
      .replace('wss:', 'https:')
      .replace('ws:', 'http:')
      .replace(/\/gl$/, '');
    return { wsUrl: wsUrlOverride, httpUrl };
  }

  const env = import.meta.env.MODE || 'development';

  switch (env) {
    case 'production':
      return SERVERS.production;
    case 'staging':
      return SERVERS.staging;
    default: {
      // Development: use same origin with /gl path - Vite proxies to backend
      // This fixes Docker Desktop external access issues on mobile
      const host = typeof window !== 'undefined' ? window.location.host : 'localhost:3000';
      const protocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const httpProtocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
      return {
        wsUrl: `${protocol}//${host}/gl`,
        httpUrl: `${httpProtocol}//${host}`,
      };
    }
  }
}

// =============================================================================
// CONNECTION SETTINGS
// =============================================================================

/** WebSocket connection settings */
export const CONNECTION = {
  /** Connection timeout (ms) */
  connectTimeout: 10000,

  /** Authentication timeout (ms) */
  authTimeout: 5000,

  /** Enable auto-reconnect */
  reconnect: true,

  /** Initial reconnect delay (ms) */
  reconnectDelay: 1000,

  /** Maximum reconnect delay (ms) */
  reconnectDelayMax: 30000,

  /** Maximum reconnect attempts (0 = infinite) */
  reconnectAttempts: 0,

  /** Heartbeat interval (ms) - sends keepalive to echo service */
  heartbeatInterval: 30000,

  /** Max missed heartbeats before forcing reconnect */
  heartbeatMissedMax: 2,

  /** Account keepalive interval (ms) - sends # account to refresh ACC cache TTL */
  accountKeepAliveInterval: 60000,
};

// =============================================================================
// TIMEOUTS
// =============================================================================

/** Protocol timeouts */
export const TIMEOUTS = {
  /** Message response timeout (ms) */
  responseTimeout: 10000,
};

// =============================================================================
// RETRY SETTINGS
// =============================================================================

/** Retry configuration for failed requests */
export const RETRY = {
  /** Maximum retry attempts */
  maxAttempts: 3,

  /** Base delay between retries (ms) */
  baseDelay: 1000,

  /** Delay multiplier for exponential backoff */
  multiplier: 2,

  /** Maximum delay (ms) */
  maxDelay: 10000,
};

// =============================================================================
// SERVICE GLIDS
// =============================================================================

/** Well-known service addresses (GLIDs) */
export const GLIDS = {
  /** System service (for auth, service messages) */
  SYSTEM: '################################',

  /** Main game manager */
  MANAGER: 'manager1',

  /** Table croupier (game controller) - dynamic per table */
  CROUPIER: 'croupier',

  /** Account service */
  ACCOUNT: 'account',

  /** Profile service */
  PROFILER: 'profiler',

  /** Echo service (dev only) */
  ECHO: 'echo',

  /** Inbox service (player notifications) */
  INBOX: 'inbox',

  /** Daily bonus service */
  DAILY_BONUS: 'daily_bonus',

  /** XP / Level progression service */
  XP: 'xp',
};

// =============================================================================
// RATE LIMITING
// =============================================================================

/** Rate limiting configuration */
export const RATE_LIMITS = {
  /** Maximum messages per second */
  messagesPerSecond: 10,

  /** Maximum chat messages per minute */
  chatPerMinute: 20,

  /** Minimum time between actions (ms) */
  actionCooldown: 200,
};

// =============================================================================
// DEBUG
// =============================================================================

/** Debug settings */
export const DEBUG = {
  /** Log all network messages */
  logMessages: import.meta.env.DEV,

  /** Log connection state changes */
  logStateChanges: import.meta.env.DEV,

  /** Simulate network latency (ms, 0 = disabled) */
  simulatedLatency: 0,

  /** Simulate packet loss (0-1, 0 = disabled) */
  simulatedPacketLoss: 0,
};
